frappe.provide("erpnext.accounts"); // @dokos

/**
 * PaymentRequestPanel – a slide-in side panel to ask a customer to pay online
 * directly from a Sales Invoice, Sales Order or Subscription, without ever
 * opening the technical Payment Request form. // @dokos
 *
 * Usage:
 *   new erpnext.accounts.PaymentRequestPanel({ frm, context }).show();
 *
 * `context` is the payload of
 *   erpnext.accounts.doctype.payment_request.payment_request.get_payment_request_context
 * and carries the *resolved* target document (for a Subscription this is the
 * current Sales Invoice / Sales Order), the amount due and sensible defaults.
 *
 * The single primary action creates AND submits the Payment Request and sends
 * the payment-link email in one step (backend: create_and_submit_payment_request).
 */
erpnext.accounts.PaymentRequestPanel = class PaymentRequestPanel {
	constructor({ frm, context }) {
		this.frm = frm;
		this.context = context || {};
		this.controls = {};
		this.wraps = {};
		this.schedules = [];
		this.amount = flt(this.context.amount_due);
		this.currency = this.context.currency || frm.doc.currency;

		this.overlay = new frappe.views.dodock_drawer({
			animation_type: "slide",
			backdrop: true,
			open_width: frappe.is_mobile() ? "100vw" : "420px",
			parent: $(this.frm.page.wrapper).find(".page-overlay"),
		});

		// @dokos: the drawer only slides; the shared panel scaffold draws header, body and footer
		this.panel = erpnext.accounts.mount_drawer_panel(this.overlay, { title: __("Request Payment") });

		this._render_header();
		this._render_body();
		this._render_footer();

		if (this.context.has_schedules) {
			this._load_schedules();
		}
	}

	// ── DOM construction ──────────────────────────────────────────────────

	// @dokos: amount pill in the panel header (amber: money still owed), document + party in the body
	_render_header() {
		this.$amount_pill = $(`<span class="dk-num"></span>`).insertAfter(this.panel.$title);
		this._render_amount();

		$(`<div class="dk-muted"></div>`)
			.text(
				[this.context.reference_name || this.frm.doc.name, this.context.party_name]
					.filter(Boolean)
					.join(" · ")
			)
			.appendTo(this.panel.$body);
	}

	_render_amount() {
		this.$amount_pill.html(
			erpnext.dokos.ui.pill.html({
				label: format_currency(this.amount, this.currency),
				theme: "amber",
				title: __("Amount"),
			})
		);
	}

	// @dokos: inline messages, schedules and fields in dk-panel__section blocks
	_render_body() {
		this.$messages = $(`<div class="dk-panel__section"></div>`).appendTo(this.panel.$body).hide();

		// Schedules (rendered lazily into this container when applicable)
		this.$schedules = $(`<div class="dk-panel__section dk-field"></div>`)
			.appendTo(this.panel.$body)
			.hide();
		this.$fields = $(`<div class="dk-panel__section"></div>`).appendTo(this.panel.$body);

		this._add_control({
			fieldtype: "Data",
			fieldname: "email_to",
			label: __("Send To"),
			options: "Email",
			default: this.context.email_to || "",
			reqd: 1,
		});

		// @dokos: say why the recipient is empty, instead of only failing on submit
		if (!this.context.email_to) {
			this.$fields.append(
				erpnext.dokos.ui.alert({
					theme: "yellow",
					title: __(
						"{0} has no default email address. Enter one above, or add an email to the primary contact of the customer.",
						[this.context.party_name || __("The customer")]
					),
				})
			);
		}

		this._add_control({
			fieldtype: "Link",
			fieldname: "email_template",
			label: __("Email Template"),
			options: "Email Template",
			onchange: () => this._apply_template(),
		});

		this._add_control({
			fieldtype: "Data",
			fieldname: "subject",
			label: __("Subject"),
			default: __("Payment Request for {0}", [this.context.reference_name || this.frm.doc.name]),
			reqd: 1,
		});

		this._add_control({
			fieldtype: "Text Editor",
			fieldname: "message",
			label: __("Message"),
		});

		this._add_control({
			fieldtype: "Check",
			fieldname: "attach_pdf",
			label: __("Attach {0} PDF", [__(this.context.reference_doctype || this.frm.doctype)]),
		});

		if (!this.context.default_payment_gateway) {
			this.$fields.append(
				erpnext.dokos.ui.alert({
					theme: "blue",
					title: __(
						"No payment gateway is configured by default — the recipient will choose an available method on the payment page."
					),
				})
			);
		}

		// Escape hatch – the technical Payment Request form
		$(`<div class="dk-panel__section"></div>`)
			.append(
				erpnext.dokos.ui.button({
					label: __("Open full Payment Request form"),
					icon: "external-link",
					variant: "ghost",
					onclick: () => this._open_full_form(),
				})
			)
			.appendTo(this.panel.$body);
	}

	// @dokos: subtle Cancel, subtle "create only" and ONE solid primary action in the sticky footer
	_render_footer() {
		this.panel.$footer.append(
			erpnext.dokos.ui.button({ label: __("Cancel"), onclick: () => this.hide() }),
			erpnext.dokos.ui.button({
				label: __("Create without Sending"),
				loading_label: __("Creating…"),
				onclick: () => this._submit(false),
			}),
			erpnext.dokos.ui.button({
				label: __("Send Payment Link"),
				loading_label: __("Sending…"),
				variant: "solid",
				onclick: () => this._submit(true),
			})
		);
	}

	// ── Schedules ─────────────────────────────────────────────────────────

	async _load_schedules() {
		const { message } = await frappe.call({
			method: "erpnext.accounts.doctype.payment_request.payment_request.get_available_payment_schedules",
			args: {
				reference_doctype: this.context.reference_doctype,
				reference_name: this.context.reference_name,
			},
		});

		if (!message?.length) return;

		this.schedules = message.map((row) => ({ ...row, __selected: true }));

		// @dokos: dk-field label + a dk-table of checkable schedule rows
		this.$schedules.empty().show();
		$(`<label></label>`).text(__("Payment Schedules")).appendTo(this.$schedules);
		const $tbody = $(`<tbody></tbody>`);
		$(`<div class="dk-table-wrap"><table class="dk-table"></table></div>`)
			.appendTo(this.$schedules)
			.find("table")
			.append($tbody);

		this.schedules.forEach((row, idx) => {
			const $row = $(`<tr class="dk-clickable">
				<td class="dk-act"><input type="checkbox" checked></td>
				<td class="prp-schedule-term"></td>
				<td class="dk-r"></td>
			</tr>`).appendTo($tbody);
			$row.find(".prp-schedule-term").text(`${row.payment_term || row.name} · ${row.due_date || ""}`);
			$row.find(".dk-r").text(format_currency(row.payment_amount, this.currency));

			const $cb = $row.find("input");
			$cb.on("change", () => {
				this.schedules[idx].__selected = $cb.prop("checked");
				this._recompute_amount();
			});
			// the whole row toggles, like the <label> it replaces
			$row.on("click", (e) => {
				if (e.target !== $cb[0]) $cb.prop("checked", !$cb.prop("checked")).trigger("change");
			});
		});

		this._recompute_amount();
	}

	_recompute_amount() {
		if (this.schedules.length) {
			this.amount = this.schedules
				.filter((r) => r.__selected)
				.reduce((sum, r) => sum + flt(r.payment_amount), 0);
		}
		this._render_amount(); // @dokos
	}

	_selected_schedules() {
		return this.schedules.filter((r) => r.__selected);
	}

	// ── Email template ────────────────────────────────────────────────────

	async _apply_template() {
		const template = this.controls.email_template.get_value();
		if (!template) return;

		const r = await frappe.xcall("erpnext.accounts.doctype.payment_request.payment_request.get_message", {
			doc: JSON.stringify({
				reference_doctype: this.context.reference_doctype,
				reference_name: this.context.reference_name,
				grand_total: this.amount,
				currency: this.currency,
			}),
			template,
		});

		if (r?.subject) this.controls.subject.set_value(r.subject);
		if (r?.message) this.controls.message.set_value(r.message);
	}

	// ── Controls ──────────────────────────────────────────────────────────

	// @dokos: controls go into the fields section of the panel
	_add_control(df) {
		const wrap = document.createElement("div");
		this.$fields.append(wrap);

		const ctrl = frappe.ui.form.make_control({ df, parent: wrap, render_input: true });
		ctrl.refresh();
		if (df.default != null) {
			ctrl.set_value(df.default);
		}
		this.controls[df.fieldname] = ctrl;
		this.wraps[df.fieldname] = wrap;
		return ctrl;
	}

	_get_values() {
		const vals = {};
		Object.entries(this.controls).forEach(([k, c]) => (vals[k] = c.get_value()));
		return vals;
	}

	// ── Visibility ────────────────────────────────────────────────────────

	show() {
		this._set_messages([]); // @dokos
		this.overlay.open();
		this.controls.email_to?.$input?.focus();
	}

	hide() {
		this.overlay.close();
	}

	destroy() {
		this.overlay.close();
		// @dokos: the drawer element is `sidebar` (there is no `wrapper`), plus its backdrop
		$(this.overlay.sidebar).remove();
		$(this.overlay._backdropEl).remove();
	}

	_open_full_form() {
		this.hide();
		frappe.new_doc("Payment Request", {
			reference_doctype: this.context.reference_doctype,
			reference_name: this.context.reference_name,
		});
	}

	// ── Submit ────────────────────────────────────────────────────────────

	async _submit(send_email) {
		const vals = this._get_values();
		const errors = [];

		if (send_email && !vals.email_to) errors.push(__("A recipient email is required to send the link"));
		if (this.schedules.length && !this._selected_schedules().length)
			errors.push(__("Select at least one payment schedule"));
		if (!flt(this.amount)) errors.push(__("Amount must be greater than 0"));

		// @dokos: validation problems show inline in the panel, not in a modal
		this._set_messages(errors);
		if (errors.length) return;

		try {
			const result = await frappe.xcall(
				"erpnext.accounts.doctype.payment_request.payment_request.create_and_submit_payment_request",
				{
					reference_doctype: this.context.reference_doctype,
					reference_name: this.context.reference_name,
					recipient_id: vals.email_to || null,
					mode_of_payment: vals.mode_of_payment || null,
					subject: vals.subject || null,
					message: vals.message || null,
					print_format: vals.attach_pdf ? this.context.default_print_format : null,
					schedules: this.schedules.length ? JSON.stringify(this._selected_schedules()) : null,
					send_email: send_email ? 1 : 0,
				}
			);

			if (result?.name) {
				frappe.show_alert(
					{
						message: send_email
							? __("Payment link sent · {0}", [
									`<a href="/app/payment-request/${result.name}">${result.name}</a>`,
							  ])
							: __("Payment Request {0} created", [
									`<a href="/app/payment-request/${result.name}">${result.name}</a>`,
							  ]),
						indicator: "green",
					},
					7
				);
				this.hide();
				this.frm.reload_doc();
			}
		} catch (e) {
			// @dokos: the server error is already shown by frappe.call; keep the buttons usable
			console.error(e);
		}
	}

	// @dokos: inline outcome banners at the top of the panel body (server exceptions keep msgprint)
	_set_messages(errors) {
		this.$messages.empty().toggle(errors.length > 0);
		errors.forEach((title) => this.$messages.append(erpnext.dokos.ui.alert({ title, theme: "red" })));
		if (errors.length) this.$messages[0].scrollIntoView({ block: "nearest" });
	}
};
